import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import SEO from '../../components/SEO';
import { 
  Building2, 
  Calendar, 
  Clock, 
  ArrowLeft,
  AlertTriangle,
  CheckCircle,
  Shield,
  FileText,
  TrendingDown,
  Scale,
  Users,
  Search
} from 'lucide-react';

export default function DefaillancesSecurisationArticle() {
  return (
    <>
      <SEO 
        title="Défaillances Entreprises : Sécurisation Marchés Publics 2025"
        description="Face aux défaillances d'entreprises, les collectivités renforcent les procédures de sécurisation des marchés publics."
        path="/blog/defaillances-securisation-marches-publics-2025"
      />
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-gradient-to-r from-violet-900 via-indigo-900 to-slate-900 text-white py-4">
        <div className="container mx-auto px-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center">
              <Building2 className="w-6 h-6" />
            </div>
            <span className="font-bold text-xl">Mon DCE Express</span>
          </Link>
          <div className="flex gap-4">
            <Link to="/login">
              <Button variant="ghost" className="text-white hover:bg-white/10">Connexion</Button>
            </Link>
            <Link to="/register">
              <Button className="bg-white text-violet-900 hover:bg-slate-100">S'inscrire</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Article */}
      <article className="py-12">
        <div className="container mx-auto px-4 max-w-4xl">
          {/* Breadcrumb */}
          <Link to="/blog" className="inline-flex items-center gap-2 text-violet-600 hover:text-violet-700 mb-8">
            <ArrowLeft className="w-4 h-4" />
            Retour au blog
          </Link>

          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <span className="px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-700">
                Juridique
              </span>
              <span className="px-2 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-violet-600 to-indigo-600 text-white">
                NOUVEAU
              </span>
            </div>
            <h1 className="text-4xl font-bold text-slate-900 mb-4">
              Défaillances d'Attributaires : Sécuriser Vos Marchés en 2026
            </h1>
            <div className="flex items-center gap-6 text-slate-500">
              <span className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                11 février 2026
              </span>
              <span className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                10 min de lecture
              </span>
            </div>
          </div>

          {/* Featured Image */}
          <div className="bg-gradient-to-br from-red-500 to-rose-600 rounded-2xl p-12 mb-12 text-white text-center">
            <AlertTriangle className="w-20 h-20 mx-auto mb-4 opacity-90" />
            <p className="text-xl font-medium">Risques économiques et sécurisation juridique</p>
            <p className="text-red-100">Nouveau mécanisme du 2ème candidat - Décret 2025</p>
          </div>

          {/* Content */}
          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-slate-600 leading-relaxed mb-8">
              La vague de défaillances d'entreprises touche de plein fouet les marchés publics. 
              En réponse, le nouveau décret autorise désormais les acheteurs à <strong>contracter directement 
              avec le 2ème candidat classé</strong> en cas de défaillance de l'attributaire. Comment les 
              acheteurs et les entreprises peuvent-ils sécuriser leurs marchés ? Analyse complète.
            </p>

            <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-lg mb-8">
              <div className="flex items-start gap-3">
                <TrendingDown className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-bold text-red-900 mb-2">Contexte économique tendu - Impact 7/10</h4>
                  <p className="text-red-800">
                    La contrainte budgétaire des collectivités, la pression sur les prix et l'inflation 
                    fragilisent de nombreux titulaires de marchés. Le risque de défaillance en cours 
                    d'exécution augmente significativement.
                  </p>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-6 flex items-center gap-3">
              <Scale className="w-7 h-7 text-red-600" />
              Le nouveau mécanisme du 2ème candidat
            </h2>

            <p>
              Le décret du 29 décembre 2025 introduit une <strong>innovation majeure</strong> : en cas de 
              défaillance de l'attributaire (liquidation judiciaire, incapacité à exécuter), l'acheteur 
              peut désormais attribuer le marché au candidat classé 2ème sans relancer de procédure.
            </p>

            <div className="bg-slate-100 rounded-xl p-6 my-8">
              <h4 className="font-bold text-slate-900 mb-4">Conditions d'application</h4>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-1" />
                  <span>La défaillance doit être <strong>constatée officiellement</strong> (jugement, résiliation pour faute)</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-1" />
                  <span>Le 2ème candidat doit avoir maintenu son offre valide</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-1" />
                  <span>L'acheteur doit vérifier que les conditions économiques de l'offre restent acceptables</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-1" />
                  <span>La procédure initiale doit être documentée et conservée</span>
                </li>
              </ul>
            </div>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-6 flex items-center gap-3">
              <Search className="w-7 h-7 text-red-600" />
              Détecter les Offres Anormalement Basses (OAB)
            </h2>

            <p>
              La défaillance d'un attributaire est souvent le résultat d'une <strong>offre anormalement basse</strong> 
              acceptée par l'acheteur. La détection des OAB devient un enjeu majeur de sécurisation.
            </p>

            <div className="grid md:grid-cols-2 gap-6 my-8">
              <div className="bg-white border-2 border-red-200 rounded-xl p-6">
                <h4 className="font-bold text-red-900 mb-3">Signaux d'alerte pour les acheteurs</h4>
                <ul className="text-slate-600 space-y-2 text-sm">
                  <li>• Prix inférieur de 25%+ à la moyenne des offres</li>
                  <li>• Prix inférieur à l'estimation initiale de 30%+</li>
                  <li>• Incohérence entre prix unitaires et forfaits</li>
                  <li>• Absence de justification des coûts de main-d'œuvre</li>
                  <li>• Entreprise en difficulté financière connue</li>
                </ul>
              </div>
              <div className="bg-white border-2 border-green-200 rounded-xl p-6">
                <h4 className="font-bold text-green-900 mb-3">Bonnes pratiques de vérification</h4>
                <ul className="text-slate-600 space-y-2 text-sm">
                  <li>• Demander une décomposition détaillée du prix</li>
                  <li>• Vérifier les références récentes du candidat</li>
                  <li>• Analyser les comptes de l'entreprise (BODACC)</li>
                  <li>• Consulter les bases de données de défaillances</li>
                  <li>• Utiliser des outils IA de détection OAB</li>
                </ul>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-6 flex items-center gap-3">
              <Shield className="w-7 h-7 text-red-600" />
              Stratégies de sécurisation pour les acheteurs
            </h2>

            <div className="space-y-4 my-8">
              <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
                <div className="w-8 h-8 bg-red-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">1</div>
                <div>
                  <h4 className="font-bold text-slate-900">Renforcer l'analyse des prix</h4>
                  <p className="text-slate-600">Systématisez la demande de justification des prix pour les offres inférieures de 15% à la moyenne. Documentez l'analyse dans le rapport d'attribution.</p>
                </div>
              </div>
              <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
                <div className="w-8 h-8 bg-red-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">2</div>
                <div>
                  <h4 className="font-bold text-slate-900">Prévoir des clauses de sauvegarde</h4>
                  <p className="text-slate-600">Intégrez dans les CCAP des clauses de résiliation facilitée en cas de difficultés manifestes d'exécution et des pénalités dissuasives.</p>
                </div>
              </div>
              <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
                <div className="w-8 h-8 bg-red-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">3</div>
                <div>
                  <h4 className="font-bold text-slate-900">Conserver les offres des candidats classés</h4>
                  <p className="text-slate-600">Gardez les dossiers des 2ème et 3ème candidats pendant toute la durée du marché pour activer le nouveau mécanisme si nécessaire.</p>
                </div>
              </div>
              <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
                <div className="w-8 h-8 bg-red-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">4</div>
                <div>
                  <h4 className="font-bold text-slate-900">Assurer un suivi d'exécution renforcé</h4>
                  <p className="text-slate-600">Mettez en place des points d'étape réguliers, des indicateurs de performance et une veille sur la santé financière du titulaire.</p>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-6 flex items-center gap-3">
              <Users className="w-7 h-7 text-red-600" />
              Conseils pour les entreprises soumissionnaires
            </h2>

            <p>
              En tant qu'entreprise, comment vous protéger et profiter des opportunités créées par 
              les défaillances de concurrents ?
            </p>

            <div className="grid md:grid-cols-2 gap-6 my-8">
              <div className="bg-green-50 border border-green-200 rounded-xl p-6">
                <h4 className="font-bold text-green-900 mb-3">Être le 2ème candidat fiable</h4>
                <ul className="text-green-800 space-y-2 text-sm">
                  <li>• Maintenez votre offre valide dans la durée</li>
                  <li>• Restez en contact avec l'acheteur</li>
                  <li>• Documentez votre capacité à reprendre le marché</li>
                  <li>• Préparez une organisation de reprise rapide</li>
                </ul>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                <h4 className="font-bold text-blue-900 mb-3">Proposer des prix réalistes</h4>
                <ul className="text-blue-800 space-y-2 text-sm">
                  <li>• Évitez les prix cassés pour "remporter"</li>
                  <li>• Justifiez vos tarifs de manière transparente</li>
                  <li>• Prévoyez des marges de sécurité</li>
                  <li>• Valorisez la qualité et la fiabilité</li>
                </ul>
              </div>
            </div>

            <div className="bg-violet-50 border border-violet-200 rounded-xl p-6 my-8">
              <h4 className="font-bold text-violet-900 mb-3">Opportunité : les marchés en reprise</h4>
              <p className="text-violet-800">
                La multiplication des défaillances crée un nouveau segment : les <strong>marchés à reprendre</strong>. 
                Les acheteurs cherchent des entreprises fiables capables d'intervenir rapidement. 
                Positionnez-vous comme un partenaire de confiance pour ces situations de crise.
              </p>
            </div>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-6 flex items-center gap-3">
              <FileText className="w-7 h-7 text-red-600" />
              Alerte prix UGAP : le gouvernement réagit
            </h2>

            <p>
              Face à la multiplication des prix aberrants, le gouvernement a annoncé au printemps 2026 
              l'activation d'une <strong>"alerte prix"</strong> au sein de l'UGAP (Union des Groupements d'Achats Publics).
            </p>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 my-8">
              <h4 className="font-bold text-amber-900 mb-3">Mesures de l'alerte prix UGAP</h4>
              <ul className="text-amber-800 space-y-2">
                <li>• Surveillance automatisée des écarts de prix significatifs</li>
                <li>• Obligation de justification renforcée pour les offres atypiques</li>
                <li>• Mise en place d'un référentiel de prix par catégorie de fournitures</li>
                <li>• Partage des signalements entre acheteurs publics</li>
              </ul>
            </div>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-6">
              Checklist de sécurisation juridique
            </h2>

            <div className="bg-slate-100 rounded-xl p-6 my-8">
              <h4 className="font-bold text-slate-900 mb-4">Pour les acheteurs publics</h4>
              <div className="space-y-3">
                <label className="flex items-center gap-3 text-slate-700">
                  <input type="checkbox" className="w-5 h-5 rounded border-slate-300" />
                  <span>Clause de résiliation pour motif d'intérêt général dans le CCAP</span>
                </label>
                <label className="flex items-center gap-3 text-slate-700">
                  <input type="checkbox" className="w-5 h-5 rounded border-slate-300" />
                  <span>Pénalités de retard et d'inexécution proportionnées</span>
                </label>
                <label className="flex items-center gap-3 text-slate-700">
                  <input type="checkbox" className="w-5 h-5 rounded border-slate-300" />
                  <span>Garantie de bonne exécution (5% pour les travaux)</span>
                </label>
                <label className="flex items-center gap-3 text-slate-700">
                  <input type="checkbox" className="w-5 h-5 rounded border-slate-300" />
                  <span>Conservation des offres 2ème et 3ème candidats</span>
                </label>
                <label className="flex items-center gap-3 text-slate-700">
                  <input type="checkbox" className="w-5 h-5 rounded border-slate-300" />
                  <span>Procédure d'analyse des OAB documentée</span>
                </label>
                <label className="flex items-center gap-3 text-slate-700">
                  <input type="checkbox" className="w-5 h-5 rounded border-slate-300" />
                  <span>Veille sur la santé financière du titulaire</span>
                </label>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-6">
              Conclusion : anticiper plutôt que subir
            </h2>

            <p>
              Le contexte économique 2026 impose une <strong>vigilance accrue</strong> sur la solidité des attributaires. 
              Le nouveau mécanisme du 2ème candidat offre un filet de sécurité, mais la meilleure protection reste 
              la <strong>prévention</strong> : analyse rigoureuse des offres, détection des OAB, et suivi attentif de l'exécution.
            </p>

            <p>
              Pour les entreprises, c'est l'occasion de se positionner comme des <strong>partenaires fiables</strong>, 
              capables d'assurer la continuité du service public même dans des situations de crise.
            </p>
          </div>

          {/* CTA */}
          <div className="mt-12 p-8 bg-gradient-to-r from-violet-600 to-indigo-600 rounded-2xl text-white text-center">
            <h3 className="text-2xl font-bold mb-4">
              Identifiez les marchés où votre fiabilité fait la différence
            </h3>
            <p className="text-violet-100 mb-6">
              Notre IA analyse les critères de chaque marché et vous recommande ceux qui valorisent la qualité et la solidité financière.
            </p>
            <Link to="/register">
              <Button size="lg" className="bg-white text-violet-700 hover:bg-slate-100">
                Essayer gratuitement
              </Button>
            </Link>
          </div>
        </div>
      </article>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-8">
        <div className="container mx-auto px-4 text-center">
          <p>© 2026 Mon DCE Express - Tous droits réservés</p>
        </div>
      </footer>
    </div>
    </>
  );
}
